import TsrSvgIcon from '../../assets/icons';
import { InputType, KEY_ARROW_DOWN, KEY_ARROW_UP, KEY_ENTER, KEY_TAB, LabelPosition, Variant } from '../models';
import { Alignment, JustifyContents } from '../../layout/models';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { TextType } from '../models/TextType';
import { Item } from '../interfaces/Item';
import { InputPropsBase, SelectPropsBase } from '../interfaces/Inputs';
import { TextInput } from '../Text';
import { InputCheckBox } from '../InputCheckBox';
import './styles.scss';
import { DEFAULT_FOCUS_VALUE } from '../../hooks/useRoveFocus';
import { CurrencyInput } from '../CurrencyInput';
import { InputWidth } from '../models/InputWidth';
import { uniqueId } from 'lodash';
import TsrTooltip from '../../tooltip';
import { NumericInput } from '../NumericInput';
import { Virtuoso } from 'react-virtuoso';
import { letterWidth } from '../../consts/letterConsts';
import Tippy, { useSingleton } from '@tippyjs/react';

interface MultiSelectProps extends InputPropsBase<Array<Item>, Array<Item>>, SelectPropsBase {
    width?: number | string;
    searchTerm?: string;
    setFocusItem: React.Dispatch<React.SetStateAction<number>>;
    focusItem: number;
    searchRef?: React.RefObject<HTMLInputElement>;
    isSearchBar?: boolean;
    isMenuOpen?: boolean;
    isInDropdown?: boolean;
    checkedItems?: number;
    additionalInput?: InputType;
    applyAllValue?: number | string | boolean;
    useTippySingleton?: boolean;
    onAdditionalInputChange?: (newValue: Array<Item>) => void;
    applyAllValueChange?: (value: number | string | boolean) => void;
}

export const CheckboxList = React.forwardRef((props: MultiSelectProps, ref: React.LegacyRef<HTMLUListElement>) => {
    const maxWidth = 320;
    const { focusItem, setFocusItem } = props;
    const [searchTerm, setSearchTerm] = useState('');
    const [selectAll, setSelectAll] = useState(props.value.length > 0 && props.value.every(item => item.checked));
    const [source, target] = useSingleton();
    const activeRef = useRef<HTMLLIElement>(null);
    const [disableMousePointer, setDisableMousePointer] = useState(false);
    const innerSearchRef = useRef<HTMLInputElement>(null);
    const apllyAllDefaultValue =
        props.additionalInput === InputType.Currency || props.additionalInput === InputType.Numeric ? 0 : '';
    const onSelectAll = (checked: boolean) => {
        props.onChange &&
            props.onChange(
                props.value.map(item =>
                    item
                        ? {
                              ...item,
                              checked: !item.disabled && checked,
                              additionalValue: props.additionalInput ? props.applyAllValue : apllyAllDefaultValue,
                          }
                        : item,
                ),
            );
        setSelectAll(checked);
    };

    const onSelectionChange = (changedItem: Item, checked: boolean) => {
        props.onChange &&
            props.onChange(
                props.value.map(item =>
                    item.id === changedItem.id && item.additonalInfo === changedItem.additonalInfo
                        ? {
                              ...changedItem,
                              checked: checked,
                              additionalValue: props.additionalInput ? props.applyAllValue : apllyAllDefaultValue,
                          }
                        : item,
                ),
            );

        if (props.value.length) {
            setSelectAll(false);
        }
    };

    const onAdditionalValueChange = (changedItem: Item, additionalValue: boolean | string | number) => {
        props.onAdditionalInputChange &&
            props.onAdditionalInputChange(
                props.value.map(item =>
                    item.id === changedItem.id ? { ...changedItem, additionalValue: additionalValue } : item,
                ),
            );

        props.applyAllValueChange && props.applyAllValueChange(apllyAllDefaultValue);

        if (props.value.length) {
            setSelectAll(false);
        }
    };

    const onApplyAll = (additionalValue: boolean | string | number) => {
        props.onAdditionalInputChange &&
            props.onAdditionalInputChange(props.value.map(item => ({ ...item, additionalValue: additionalValue })));
        props.applyAllValueChange && props.applyAllValueChange(additionalValue);

        if (props.value.length) {
            setSelectAll(false);
        }
    };

    useEffect(() => {
        if (props.value.length > 0 && props.value.filter(item => item.checked === true).length === props.value.length) {
            setSelectAll(true);
        }
    }, [props.value]);

    useEffect(() => {
        setSearchTerm(props.searchTerm || '');
    }, [props.searchTerm]);

    const results = useMemo(
        () =>
            !searchTerm
                ? props.value
                : props.value.filter((item: { label: string }) => item.label.toLowerCase().includes(searchTerm.toLowerCase())),
        [searchTerm, props.value],
    );

    function renderSearchBar() {
        return (
            <div className="search-box">
                <TextInput
                    disabled={props.disabled === 'inputOnly' || props.disabled === true}
                    onChange={setSearchTerm}
                    value={searchTerm}
                    gap={5}
                    type={TextType.SearchBox}
                    placeholder={props.placeholder}
                    onKeyDown={e => {
                        if (e.key === 'ArrowDown') {
                            setFocusItem(0);
                        }
                    }}
                    ref={innerSearchRef}
                />
                {props.showSelectedNumbersInSearchBar && (
                    <span className="selectedItemsNumbers">
                        ({props.checkedItems}/{props.value?.length})
                    </span>
                )}

                <TsrSvgIcon icon="search" color="info" pointer />
            </div>
        );
    }

    const setTabIndex = (index: number) => {
        if (props.disabled) return undefined;
        else if (index === 0 && focusItem === DEFAULT_FOCUS_VALUE && !props.showSearchBar && !props.isInDropdown) return 0;
        else if (focusItem === index) return 0;
        else return undefined;
    };

    const onKeyDownSelectAll = (event: React.KeyboardEvent<HTMLLIElement>) => {
        setDisableMousePointer(true);
        if (event.key === KEY_ENTER) {
            onSelectAll(!selectAll);
            setFocusItem(0);
        } else if (event.key === KEY_ARROW_UP) {
            innerSearchRef?.current?.focus();
            props.searchRef?.current?.focus();
            props.isSearchBar && setFocusItem(DEFAULT_FOCUS_VALUE);
        } else if (event.key === KEY_TAB) {
            setFocusItem(DEFAULT_FOCUS_VALUE);
        }
    };

    const onKeyDown = (event: React.KeyboardEvent<HTMLLIElement>, item: Item, focusedIndex: number) => {
        setDisableMousePointer(true);
        if (event.key === KEY_ENTER) {
            onSelectionChange(item, !item.checked);
            setFocusItem(focusedIndex);
        } else if (event.key === KEY_ARROW_UP && focusedIndex === 0 && props.hideSelectAll) {
            innerSearchRef?.current?.focus();
            props.searchRef?.current?.focus();
            props.isSearchBar && setFocusItem(DEFAULT_FOCUS_VALUE);
        } else if (event.key === KEY_TAB) {
            setFocusItem(DEFAULT_FOCUS_VALUE);
        } else if (event.key === KEY_ARROW_DOWN && focusedIndex === 0 && !props.isInDropdown && focusItem === 0) {
            setFocusItem(1);
        } else {
            event.preventDefault();
        }
    };

    const isAdditionalInputActive = () => document.activeElement?.tagName.toLowerCase() === 'input';

    const renderAdditionalInput = (additionInput: InputType, item: Item) => {
        switch (additionInput) {
            case InputType.Currency:
                return (
                    <CurrencyInput
                        value={item.additionalValue as number}
                        onChange={value => onAdditionalValueChange(item, value)}
                        min={0}
                        disabled={!item.checked || props.disabled}
                        width={InputWidth.Currency}
                        tabIndex={-1}
                    />
                );
            case InputType.Numeric:
                return (
                    <NumericInput
                        value={item.additionalValue as number}
                        onChange={value => onAdditionalValueChange(item, value)}
                        min={0}
                        disabled={!item.checked || props.disabled}
                        width={InputWidth.Numeric}
                        tabIndex={-1}
                    />
                );
            default:
                return <></>;
        }
    };

    const renderApplyToAllInput = (additionInput: InputType) => {
        switch (additionInput) {
            case InputType.Currency:
                return (
                    <CurrencyInput
                        value={props.applyAllValue as number}
                        onChange={value => onApplyAll(value)}
                        min={0}
                        width={InputWidth.Currency}
                        disabled={!props.value.some(item => item.checked)}
                        tabIndex={-1}
                    />
                );
            case InputType.Numeric:
                return (
                    <NumericInput
                        value={props.applyAllValue as number}
                        onChange={value => onApplyAll(value)}
                        min={0}
                        width={InputWidth.Numeric}
                        disabled={!props.value.some(item => item.checked)}
                        tabIndex={-1}
                    />
                );
            default:
                return <></>;
        }
    };

    useEffect(() => {
        const listLength = props.hideSelectAll ? results.length : results.length + 1;
        !activeRef.current && focusItem === 0 && setFocusItem(DEFAULT_FOCUS_VALUE);
        focusItem === listLength && setFocusItem(focusItem - 1);
        activeRef.current?.focus();
    }, [focusItem]);

    const currentWidth = useMemo(() => {
        const maxLength = Math.max(...(results as Array<Item>).map(el => (el as Item).label.length)) * letterWidth;
        const selectAllLength = props.selectAllText ? props.selectAllText?.length * letterWidth + 30 : 0;
        return Math.max(...[maxLength, selectAllLength, props.width as number]) > maxWidth
            ? maxWidth
            : Math.max(...[maxLength, selectAllLength, props.width as number]);
    }, [results, props.width, props.selectAllText]);
    return (
        <>
            {props.showSearchBar && renderSearchBar()}
            <ul
                className={`${props.isInDropdown && 'dropdown-dlc'} select-list ${
                    props.variant === Variant.AgGrid ? 'agGrid ignoreOutsideClick' : ''
                }`}
                style={{
                    height: props.rowsToShow,
                    width: props.isInDropdown ? currentWidth : props.width,
                }}
                ref={ref}
            >
                {results.length > 0 && !props.hideSelectAll && (
                    <li
                        className={
                            (props.disabled ? 'list-item-disabled' : 'list-item') +
                            (props.className ? ' ' + props.className : '') +
                            (disableMousePointer ? ' disableMouse' : '') +
                            (props.additionalInput ? ' additionalInputItem' : '')
                        }
                        onClick={() => {
                            onSelectAll(!selectAll);
                        }}
                        onKeyDown={event => !isAdditionalInputActive() && onKeyDownSelectAll(event)}
                        onMouseMove={e => {
                            setDisableMousePointer(false);
                            !isAdditionalInputActive() && setFocusItem(0);
                        }}
                        onMouseEnter={() => {
                            !disableMousePointer && !isAdditionalInputActive() && setFocusItem && setFocusItem(0);
                        }}
                        ref={focusItem === 0 ? activeRef : undefined}
                        tabIndex={focusItem === 0 && !props.disabled ? 0 : undefined}
                    >
                        <InputCheckBox
                            gap={10}
                            label={props.selectAllText}
                            labelClassName="fw-bold"
                            disabled={props.disabled}
                            labelPosition={LabelPosition.Right}
                            justify={JustifyContents.flexStart}
                            align={Alignment.center}
                            uncontrolled={props.uncontrolled}
                            value={selectAll}
                            tabIndex={-1}
                        />
                        {props.additionalInput && (
                            <div className="additionalInput" onClick={event => event.stopPropagation()}>
                                {renderApplyToAllInput(props.additionalInput)}
                            </div>
                        )}
                    </li>
                )}
                {/* source is always passed: @tippyjs/react logs an error when useSingleton() source is left unused */}
                <Tippy singleton={source} delay={200} />
                <Virtuoso
                    style={{
                        maxHeight: 182,
                    }}
                    totalCount={results.length}
                    data={results}
                    itemContent={(index, item) => {
                        const focusedIndex = props.hideSelectAll ? index : index + 1;
                        return (item as Item).label.length * letterWidth >
                            (props.isInDropdown
                                ? maxWidth
                                : (ref as React.RefObject<HTMLUListElement>).current?.offsetWidth || 0) -
                                25 ? (
                            <TsrTooltip
                                visible={props.isMenuOpen && focusItem === focusedIndex}
                                content={(item as Item).label}
                                singleton={props.useTippySingleton ? target : undefined}
                            >
                                <li
                                    key={uniqueId('liCheckbox-')}
                                    className={
                                        (props.disabled === 'inputOnly' || props.disabled === true
                                            ? 'list-item-disabled'
                                            : 'list-item') +
                                        (props.className ? ' ' + props.className : '') +
                                        (disableMousePointer || props.additionalInput ? ' disableMouse' : '') +
                                        (props.additionalInput ? ' additionalInputItem' : '')
                                    }
                                    onClick={() => {
                                        if (!item.disabled) {
                                            onSelectionChange(item, !item.checked);
                                        }
                                        setFocusItem(focusedIndex);
                                    }}
                                    onKeyDown={event => {
                                        !isAdditionalInputActive() && onKeyDown(event, item, focusedIndex);
                                    }}
                                    onMouseMove={e => {
                                        setDisableMousePointer(false);
                                        !isAdditionalInputActive() && setFocusItem(focusedIndex);
                                    }}
                                    onMouseEnter={() => {
                                        !disableMousePointer && setFocusItem && setFocusItem(focusedIndex);
                                    }}
                                    onMouseLeave={() => {
                                        setFocusItem && setFocusItem(DEFAULT_FOCUS_VALUE);
                                    }}
                                    ref={focusItem === focusedIndex ? activeRef : undefined}
                                    tabIndex={setTabIndex(focusedIndex)}
                                >
                                    <InputCheckBox
                                        gap={10}
                                        label={item.label}
                                        disabled={item.disabled || props.disabled === 'inputOnly' || props.disabled === true}
                                        id={item.id}
                                        labelPosition={LabelPosition.Right}
                                        justify={JustifyContents.flexStart}
                                        align={Alignment.center}
                                        uncontrolled={props.uncontrolled}
                                        value={item.checked || false}
                                        additionalInfo={item.additonalInfo}
                                        additionalInfoClassName={item.additionalInfoClassName}
                                        width={props.additionalInput ? 'auto' : '100%'}
                                        tabIndex={-1}
                                        isUsedInDropdown
                                    />
                                    {props.additionalInput && (
                                        <div className="additionalInput" onClick={event => event.stopPropagation()}>
                                            {renderAdditionalInput(props.additionalInput, item)}
                                        </div>
                                    )}
                                </li>
                            </TsrTooltip>
                        ) : (
                            <li
                                key={uniqueId('liCheckbox-')}
                                className={
                                    (props.disabled === 'inputOnly' || props.disabled === true
                                        ? 'list-item-disabled'
                                        : 'list-item') +
                                    (props.className ? ' ' + props.className : '') +
                                    (disableMousePointer || props.additionalInput ? ' disableMouse' : '') +
                                    (props.additionalInput ? ' additionalInputItem' : '')
                                }
                                onClick={() => {
                                    if (!item.disabled) {
                                        onSelectionChange(item, !item.checked);
                                    }
                                    setFocusItem(focusedIndex);
                                }}
                                onKeyDown={event => {
                                    !isAdditionalInputActive() && onKeyDown(event, item, focusedIndex);
                                }}
                                onMouseMove={e => {
                                    setDisableMousePointer(false);
                                    !isAdditionalInputActive() && setFocusItem(focusedIndex);
                                }}
                                onMouseEnter={() => {
                                    !disableMousePointer &&
                                        !isAdditionalInputActive() &&
                                        setFocusItem &&
                                        setFocusItem(focusedIndex);
                                }}
                                ref={focusItem === focusedIndex ? activeRef : undefined}
                                tabIndex={setTabIndex(focusedIndex)}
                            >
                                <InputCheckBox
                                    gap={10}
                                    label={item.label}
                                    disabled={item.disabled || props.disabled === 'inputOnly' || props.disabled === true}
                                    id={item.id}
                                    labelPosition={LabelPosition.Right}
                                    justify={JustifyContents.flexStart}
                                    align={Alignment.center}
                                    uncontrolled={props.uncontrolled}
                                    value={item.checked || false}
                                    additionalInfo={item.additonalInfo}
                                    additionalInfoClassName={item.additionalInfoClassName}
                                    width={props.additionalInput ? 'auto' : '100%'}
                                    tabIndex={-1}
                                    isUsedInDropdown
                                />
                                {props.additionalInput && (
                                    <div className="additionalInput" onClick={event => event.stopPropagation()}>
                                        {renderAdditionalInput(props.additionalInput, item)}
                                    </div>
                                )}
                            </li>
                        );
                    }}
                />
            </ul>
        </>
    );
});
